import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CloseButton, Heading, Modal, ProgressBar, Spinner } from '@zillow/constellation-10';
import {
  ActionMetadataEntryPoint,
  type GetTourConfigAndAvailabilityWebApiResponseDto,
  type TourConfirmationType,
  TourDuration,
} from '@zg-rentals/ts-z-calendar-client';

import TourCalendarConfig from './components/TourCalendarConfig';
import TourCalendarTimeZone from './components/TourCalendarTimeZone';
import TourAvailabilityCalendarFooter from './components/TourAvailabilityCalendarFooter';
import TourCalendar from '../TourCalendar';
import { type TimeZoneIds, TOUR_CALENDAR_MODES, TourWizardSteps } from '../../constants/TourCalendar';
import { MOBILE_HANDOFF_TYPES, mobileHandoffHandler } from '../../utils/mobileAppCallbacks';
import useTourCalendarApi from '../../utils/tourCalendarHooks';
import type { ClickStreamMetadata } from '../../constants/TouringClickStreamEvents';
import {
  CALENDAR_CLICKSTREAM_ACTION,
  emitCalendarEventActionEvent,
  emitTourAvailabilityWizardImpresssionEvent,
  emitTourAvailabilityWizardTimeZoneNextClickEvent,
  emitTourTypeSelectionEvent,
  TourAvailabilityWizardImpresssionSourceMap,
} from '../../constants/TouringClickStreamEvents';

type Props = {
  onClose: () => void;
  listingAlias: string;
  modalHeaderText: string;
  isInMobileWebView?: boolean;
  modalHeaderLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  clickStreamMetadata?: ClickStreamMetadata;
  hideCloseButton?: boolean;
  canAccessPostingPathTourDuration?: boolean;
};

const TourAvailabilityWizard = ({
  onClose,
  listingAlias,
  modalHeaderText,
  isInMobileWebView = false,
  modalHeaderLevel,
  clickStreamMetadata,
  hideCloseButton = false,
  canAccessPostingPathTourDuration = false,
}: Props) => {
  const mode = TOUR_CALENDAR_MODES.TOUR_AVAILABILITY;
  const [wizardStep, setWizardStep] = useState<TourWizardSteps>(TourWizardSteps.CONFIG);
  const [wizardTourConfirmationType, setWizardTourConfirmationType] = useState<TourConfirmationType>();
  const [wizardTimeZone, setWizardTimeZone] = useState<TimeZoneIds>();
  const [wizardTourDuration, setWizardTourDuration] = useState<TourDuration | undefined>(TourDuration.MINUTES_30);
  const [hasEnabledTouring, setHasEnabledTouring] = useState(false);

  const onGetTourConfigAndAvailabilitySuccess = useCallback(
    (data: GetTourConfigAndAvailabilityWebApiResponseDto) => {
      const trackTourAvailabilityWizardImpression = (source: TourAvailabilityWizardImpresssionSourceMap) => {
        if (clickStreamMetadata) {
          emitTourAvailabilityWizardImpresssionEvent(clickStreamMetadata, source);
        }
      };

      if ('tourConfig' in data && data.tourConfig) {
        const confirmationType = data.tourConfig.tourSchedulingTypes.schedulingTypes[0].confirmationType;
        const zoneId = data.tourConfig.zoneId;

        if (confirmationType && zoneId) {
          setWizardStep?.(TourWizardSteps.CALENDAR);
          setHasEnabledTouring(true);
          trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.TOUR_EVENT);
          return;
        }

        if (confirmationType) {
          setWizardStep?.(TourWizardSteps.TIME_ZONE);
          trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TIME_NEXT);
          return;
        }

        trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TYPE_NEXT);
        return;
      }

      trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TYPE_NEXT);
    },
    [setWizardStep],
  );

  const {
    availabilityEvents,
    tourEvents,
    timeZone: timeZoneFromApi,
    tourConfirmationType: tourConfirmationTypeFromApi,
    tourDuration: tourDurationFromApi,
    addAvailabilityEvent,
    updateAvailabilityEvent,
    removeAvailabilityEvent,
    performTourAction,
    enableTouringForListing,
    isGetTourConfigAndAvailabilityLoading,
    isEnableTouringForListingLoading,
    calendarStartTime,
    calendarEndTime,
  } = useTourCalendarApi({
    listingAlias,
    mode,
    onGetTourConfigAndAvailabilitySuccess,
  });

  const renderHeader = () => {
    return (
      <Heading level={modalHeaderLevel ?? 5} data-testid="TourAvailabilityWizard-ModalHeader">
        {modalHeaderText}
      </Heading>
    );
  };

  const mobileWebViewProps = useMemo(
    () => (isInMobileWebView ? { closeButton: null, header: null } : {}),
    [isInMobileWebView],
  );

  const onTourTypeSelection = (tourConfirmationType: TourConfirmationType) => {
    setWizardTourConfirmationType(tourConfirmationType);
  };

  const onTimeZoneSelection = (timeZone: TimeZoneIds) => {
    setWizardTimeZone(timeZone);
  };

  // Initialize with existing API tour duration if available
  useEffect(() => {
    if (tourDurationFromApi) {
      setWizardTourDuration(tourDurationFromApi);
    }
  }, [tourDurationFromApi]);

  const onTourDurationSelection = (selectedTourDuration: TourDuration) => {
    setWizardTourDuration(selectedTourDuration);
  };

  const renderBody = () => {
    let component;
    let progressBarValue = 1;

    if (wizardStep === TourWizardSteps.CONFIG) {
      component = (
        <TourCalendarConfig
          onTourTypeSelection={onTourTypeSelection}
          tourConfirmationType={wizardTourConfirmationType}
        />
      );
    } else if (wizardStep === TourWizardSteps.TIME_ZONE) {
      component = (
        <TourCalendarTimeZone
          onTimeZoneSelection={onTimeZoneSelection}
          onTourDurationSelection={onTourDurationSelection}
          timeZone={wizardTimeZone}
          tourDuration={wizardTourDuration}
          disableTimeZoneSelection={Boolean(timeZoneFromApi)}
          canAccessPostingPathTourDuration={canAccessPostingPathTourDuration}
        />
      );
      progressBarValue = 2;
    } else if (timeZoneFromApi && tourConfirmationTypeFromApi) {
      component = (
        <TourCalendar
          availabilityEvents={availabilityEvents}
          tourEvents={tourEvents}
          addAvailabilityEvent={addAvailabilityEvent}
          updateAvailabilityEvent={updateAvailabilityEvent}
          removeAvailabilityEvent={removeAvailabilityEvent}
          performTourAction={performTourAction}
          mode={mode}
          timeZone={timeZoneFromApi}
          clickStreamMetadata={clickStreamMetadata}
          isInMobileWebView={isInMobileWebView}
          listingAlias={listingAlias}
          calendarStartTime={calendarStartTime}
          calendarEndTime={calendarEndTime}
          tourDuration={wizardTourDuration}
        />
      );
      progressBarValue = 3;
    }

    return (
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)' }}>
        {!hasEnabledTouring ? (
          <Box
            css={{
              gridColumnStart: { base: '2', md: '4' },
              gridColumnEnd: { base: 'span 22', md: 'span 18' },
              marginTop: { base: 'default', md: '48px' },
              marginBottom: { base: 'loose', md: 'looser' },
            }}
          >
            <ProgressBar maxValue={3} value={progressBarValue} data-testid="TourAvailabilityWizard-ProgressBar" />
          </Box>
        ) : (
          <Box
            css={{
              gridColumnStart: '1',
              gridColumnEnd: '24',
              marginTop: { base: isInMobileWebView ? undefined : 'default', md: '48px' },
            }}
          />
        )}
        {component}
      </Box>
    );
  };

  const onClickBack = () => {
    if (wizardStep === TourWizardSteps.TIME_ZONE) {
      setWizardStep(TourWizardSteps.CONFIG);
    } else if (wizardStep === TourWizardSteps.CALENDAR) {
      setWizardStep(TourWizardSteps.TIME_ZONE);
    }
  };

  const onClickNext = () => {
    if (wizardStep === TourWizardSteps.CONFIG) {
      if (clickStreamMetadata && wizardTourConfirmationType) {
        emitTourTypeSelectionEvent(clickStreamMetadata, wizardTourConfirmationType);
      }
      return setWizardStep(TourWizardSteps.TIME_ZONE);
    } else if (wizardStep === TourWizardSteps.TIME_ZONE && wizardTourConfirmationType && wizardTimeZone) {
      return enableTouringForListing(
        wizardTourConfirmationType,
        wizardTimeZone,
        wizardTourDuration,
        ActionMetadataEntryPoint.WIZARD,
      ).then(() => {
        if (clickStreamMetadata) {
          emitTourAvailabilityWizardTimeZoneNextClickEvent(clickStreamMetadata);
        }
        return setWizardStep(TourWizardSteps.CALENDAR);
      });
    } else if (wizardStep === TourWizardSteps.CALENDAR) {
      if (clickStreamMetadata) {
        try {
          emitCalendarEventActionEvent(clickStreamMetadata, CALENDAR_CLICKSTREAM_ACTION.ADD_TO_LISTING_CTA);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error tracking wizard calendar cta', error);
        }
      }

      if (isInMobileWebView) {
        return mobileHandoffHandler(MOBILE_HANDOFF_TYPES.TOUR_ADDED_SUCCEEDED);
      }

      return onClose();
    }
  };

  const isNextStepDisabled = () => {
    if (wizardStep === TourWizardSteps.CONFIG) {
      return !wizardTourConfirmationType;
    } else if (wizardStep === TourWizardSteps.TIME_ZONE) {
      return (
        !isEnableTouringForListingLoading && (!wizardTourConfirmationType || !wizardTimeZone || !wizardTourDuration)
      );
    }

    return false;
  };

  if (isGetTourConfigAndAvailabilityLoading) {
    return (
      <Box css={{ display: 'flex', justifyContent: 'center', padding: 'loose' }}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Modal
      open={true}
      onOpenChange={(open) => open === false && onClose()}
      closeButton={hideCloseButton ? null : <CloseButton />}
      header={renderHeader()}
      body={renderBody()}
      footer={
        <TourAvailabilityCalendarFooter
          isNextDisabled={isNextStepDisabled()}
          onClickBack={onClickBack}
          onClickNext={onClickNext}
          step={wizardStep}
          hasEnabledTouring={hasEnabledTouring}
        />
      }
      size="fullScreen"
      {...mobileWebViewProps}
    />
  );
};

export default TourAvailabilityWizard;
